// src/components/TrustScoreDashboard.tsx
import { useEffect, useState } from "react";
import {
  getUserTrustScore,
  getUserTrustScoreEvents,
  getUserAverageRating,
  generateReferralCode,
  logTrustScoreEvent,
  redeemReferralCode,
  getUserReferralCode,
} from "@/lib/appwrite/trustScore";
import { useUserContext } from "@/context/AuthContext";
import {
  Award,
  Zap,
  TrendingUp,
  AlertCircle,
  UserCheck,
  // ShoppingCart,
  X,
  Info,
  PlusCircle,
  MinusCircle,
  Check,
  Crown,
  Clock,
  Calendar,
} from "lucide-react";
import { getUserViolation } from "@/lib/appwrite/userViolationAPI";
import { Button, Input, useToast } from "../ui";
import { getUserActiveSubscription, useSubscriptionPayment } from "@/hooks/useSubscriptionPayment";
import { Loader } from "@/components/shared"; // Assuming this is your existing loader
import PaymentPhoneField from "@/components/shared/PaymentPhoneField";
import { paymentContactFromIdentifier, requiresPaymentPhone } from "@/lib/cashfree/paymentContact";

// Temporarily hidden. Set to true to restore the TrustScore billing summary.
const SHOW_SUBSCRIPTION_BILLING_SUMMARY = false;

const TrustScoreDashboard = () => {
  const { user } = useUserContext();
  const [trustScore, setTrustScore] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [rating, setRating] = useState<{
    average: number;
    count: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSeller, setIsSeller] = useState(false);
  const [violations, setViolations] = useState<any>(null);
  const [referralCode, setReferralCode] = useState("");
  const [generatingCode, setGeneratingCode] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [pointsToBuy, setPointsToBuy] = useState(1000);
  const [paymentAmount, setPaymentAmount] = useState(50);
  const [paymentPhone, setPaymentPhone] = useState("");

  const [userReferralCode, setUserReferralCode] = useState("");
  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [referralInput, setReferralInput] = useState("");
  const [showTipsModal, setShowTipsModal] = useState(false);

  const { handleSubscriptionPayment, isProcessing, isVerifying } =
    useSubscriptionPayment();
  const { toast } = useToast();

  // Combined loading state for payment processes
  const isPaymentLoading = isProcessing || isVerifying;
  const [pointsError, setPointsError] = useState("");

  const [activeSubscription, setActiveSubscription] = useState<any>(null);
  const [nextBillingDate, setNextBillingDate] = useState<string>("");
  const [daysUntilBilling, setDaysUntilBilling] = useState<number>(0);

  useEffect(() => {
    if (user?.id) {
      const fetchData = async () => {
        try {
          setIsSeller(Boolean(user.isSeller));

          // Get trust score data
          const scoreData = await getUserTrustScore(user.id);
          setTrustScore(scoreData);
          try {
            const subscriptionData = await getUserActiveSubscription(user.id);
            if (subscriptionData) {
              setActiveSubscription(subscriptionData);

              // Calculate next billing date (28 days from creation)
              const createdDate = new Date(subscriptionData.createdAt);
              const billingDate = new Date(
                createdDate.getTime() + 28 * 24 * 60 * 60 * 1000
              );
              setNextBillingDate(billingDate.toISOString());

              // Calculate days until billing
              const today = new Date();
              const timeDiff = billingDate.getTime() - today.getTime();
              const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
              setDaysUntilBilling(Math.max(0, daysDiff));
            }
          } catch (error) {
            console.log("No active subscription found");
          }
          // Get user violations
          const violationsData = await getUserViolation(user.id);
          setViolations(violationsData);

          // Get other data
          const [eventsData, ratingData] = await Promise.all([
            getUserTrustScoreEvents(user.id),
            getUserAverageRating(user.id),
          ]);

          setEvents(eventsData);
          setRating(ratingData);

          // Check for weekly activity bonus (no violations for 7 days)
          if (
            violationsData &&
            !violationsData.flaggedUntil &&
            violationsData.$updatedAt &&
            new Date(violationsData.$updatedAt).getTime() <
              Date.now() - 7 * 24 * 60 * 60 * 1000
          ) {
            await logTrustScoreEvent(
              user.id,
              "weekly_activity",
              10,
              "7 days without violations"
            );
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [user?.id]);

  useEffect(() => {
    const fetchUserCode = async () => {
      try {
        const response = await getUserReferralCode(user.id);
        if (response.documents.length > 0) {
          setUserReferralCode(response.documents[0].code);
        }
      } catch (error) {
        console.error("Error fetching user referral code:", error);
      }
    };
    fetchUserCode();
  }, [user.id]);

  // Check if user can redeem
  useEffect(() => {
    if (trustScore?.hasRedeemed) {
      setShowRedeemForm(false);
    }
  }, [trustScore]);

  // Redeem function
  const handleRedeem = async () => {
    try {
      const updatedScore = await redeemReferralCode(referralInput);

      toast({
        title: "Success",
        description: "200 points added to your account!",
      });

      setShowRedeemForm(false);

      setTrustScore(updatedScore);
      setEvents(await getUserTrustScoreEvents(user.id));
    } catch (error) {
      console.error("Referral redemption error:", error);
      const errorMessage =
        (error as { message?: string })?.message ||
        error?.toString() ||
        "Failed to redeem referral code";
      toast({
        title: "Failed",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const getLevelProgress = () => {
    if (!trustScore) return 0;
    if (isSeller) {
      if (trustScore.score >= 5000) return 100;
      if (trustScore.score >= 3200)
        return ((trustScore.score - 3200) / (5000 - 3200)) * 100;
      if (trustScore.score >= 2000)
        return ((trustScore.score - 2000) / (3200 - 2000)) * 100;
      if (trustScore.score >= 1100)
        return ((trustScore.score - 1100) / (2000 - 1100)) * 100;
      if (trustScore.score >= 600)
        return ((trustScore.score - 600) / (1100 - 600)) * 100;
      return (trustScore.score / 600) * 100;
    } else {
      if (trustScore.score >= 5000) return 100;
      if (trustScore.score >= 3500)
        return ((trustScore.score - 3500) / (5000 - 3500)) * 100;
      if (trustScore.score >= 2000)
        return ((trustScore.score - 2000) / (3500 - 2000)) * 100;
      if (trustScore.score >= 1000)
        return ((trustScore.score - 1000) / (2000 - 1000)) * 100;
      return (trustScore.score / 1000) * 100;
    }
  };

  const getNextLevel = () => {
    if (!trustScore) return { level: "", required: 0 };
    if (isSeller) {
      if (trustScore.score < 600)
        return { level: "Silver", required: 600 - trustScore.score };
      if (trustScore.score < 1100)
        return { level: "Gold", required: 1100 - trustScore.score };
      if (trustScore.score < 2000)
        return { level: "Diamond", required: 2000 - trustScore.score };
      if (trustScore.score < 3200)
        return { level: "Legend", required: 3200 - trustScore.score };
      return { level: "Max", required: 0 };
    } else {
      if (trustScore.score < 1000)
        return { level: "Silver", required: 1000 - trustScore.score };
      if (trustScore.score < 2000)
        return { level: "Gold", required: 2000 - trustScore.score };
      if (trustScore.score < 3500)
        return { level: "Diamond", required: 3500 - trustScore.score };
      if (trustScore.score < 5000)
        return { level: "Legend", required: 5000 - trustScore.score };
      return { level: "Max", required: 0 };
    }
  };

  const getFeeReduction = () => {
    if (!trustScore) return "0%";
    if (isSeller) {
      if (trustScore.score >= 5000) return "0%";
      if (trustScore.score >= 3200) return "0%";
      if (trustScore.score >= 2000) return "0%";
      if (trustScore.score >= 1100) return "0%";
      if (trustScore.score >= 600) return "0%";
      return "0%";
    } else {
      if (trustScore.score >= 5000) return "0%";
      if (trustScore.score >= 3500) return "0%";
      if (trustScore.score >= 2000) return "0%";
      if (trustScore.score >= 1000) return "0%";
      return "0%";
    }
  };

  // const getNextFeeReduction = () => {
  //   const nextLevel = getNextLevel();
  //   if (nextLevel.level === "Max") return "Max Level";

  //   if (isSeller) {
  //     switch (nextLevel.level) {
  //       case "Silver":
  //         return "0%";
  //       case "Gold":
  //         return "0%";
  //       case "Diamond":
  //         return "0%";
  //       case "Legend":
  //         return "0%";
  //       default:
  //         return "";
  //     }
  //   } else {
  //     switch (nextLevel.level) {
  //       case "Silver":
  //         return "0%";
  //       case "Gold":
  //         return "0%";
  //       case "Diamond":
  //         return "0%";
  //       case "Legend":
  //         return "0%";
  //       default:
  //         return "";
  //     }
  //   }
  // };

  const getLevelColor = () => {
    if (!trustScore) return "bg-gray-200 text-gray-800";
    switch (trustScore.level) {
      case "bronze":
        return "bg-amber-600 text-white";
      case "silver":
        return "bg-gray-300 text-gray-800";
      case "gold":
        return "bg-yellow-400 text-yellow-800";
      case "diamond":
        return "bg-blue-300 text-blue-800";
      case "legend":
        return "bg-purple-500 text-white";
      default:
        return "bg-gray-200 text-gray-800";
    }
  };

  const handleGenerateReferralCode = async () => {
    setGeneratingCode(true);
    try {
      const code = await generateReferralCode(user.id);
      setReferralCode(code);
      setTimeout(() => {
        // Auto-save after 5 seconds
      }, 5000);
    } catch (error) {
      console.error("Error generating referral code:", error);
      toast({
        title: "Failure",
        description: "Failed to generate referral code",
        variant: "destructive",
      });
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleCopyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(userReferralCode);
      toast({
        title: "Copied!",
        description: "Referral code copied to clipboard",
      });
    } catch (error) {
      console.error("Failed to copy referral code:", error);
      toast({
        title: "Failed",
        description: "Failed to copy referral code. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleBuyPoints = async () => {
    if (pointsToBuy < 250) {
      setPointsError("Minimum 250 points required");
      return;
    }
    setPointsError("");
    try {
      await handleSubscriptionPayment(
        user.id,
        paymentAmount, // amount
        pointsToBuy,
        { name: user.name, emailOrPhone: user.emailOrPhone, paymentPhone },
        "trust_points"
      );

      setShowBuyModal(false);
      // Refresh trust score data after successful payment
      const scoreData = await getUserTrustScore(user.id);
      setTrustScore(scoreData);
    } catch (error) {
      console.error("Error buying points:", error);
      toast({
        title: "Failed",
        description: "Failed to purchase points. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Calculate payment amount when points change
  useEffect(() => {
    setPaymentAmount(pointsToBuy / 10);
  }, [pointsToBuy]);

  if (loading)
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-pulse text-gray-500">Loading TrustScore...</div>
      </div>
    );

  if (!trustScore)
    return (
      <div className="p-6 text-center text-gray-700">
        No trust score data found
      </div>
    );

  const nextLevel = getNextLevel();

  return (
    <div className="p-4 space-y-6 max-w-4xl mx-auto relative">
      {/* Payment Loader Overlay */}
      {isPaymentLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center">
            <Loader />
            <p className="mt-4 text-gray-700 font-medium">
              {isProcessing ? "Processing Payment..." : "Verifying Payment..."}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Please don't close this window
            </p>
          </div>
        </div>
      )}

      {SHOW_SUBSCRIPTION_BILLING_SUMMARY && activeSubscription && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-medium text-blue-800">
                  Active Subscription
                </h3>
                <p className="text-sm text-gray-600">
                  {activeSubscription.points} points purchased for ₹
                  {activeSubscription.amount}
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-orange-600">
                <Clock className="h-4 w-4" />
                <span className="font-medium">
                  {daysUntilBilling} days left
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Next billing: {new Date(nextBillingDate).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Main TrustScore Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Your TrustScore
              </h2>
              <div className="flex items-center mt-3">
                <span className="text-4xl font-bold text-gray-900 mr-3">
                  {trustScore.score}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getLevelColor()}`}>
                  {trustScore.level}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isSeller
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}>
                  {isSeller ? "Seller" : "Buyer"}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {!isSeller && "Switch to seller dashboard after first sale"}
              </p>
            </div>

            {rating && rating.count > 0 && (
              <div className="mt-4 md:mt-0">
                <h3 className="text-sm font-medium text-gray-500">
                  Average Rating
                </h3>
                <div className="flex items-center">
                  <span className="text-3xl font-bold text-gray-900 mr-2">
                    {rating.average.toFixed(1)}
                  </span>
                  <div className="flex flex-col">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={`text-xl ${
                            star <= Math.round(rating.average)
                              ? "text-yellow-400"
                              : "text-gray-300"
                          }`}>
                          ★
                        </span>
                      ))}
                    </div>
                    <span className="text-sm text-gray-500">
                      from {rating.count} reviews
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-700">
                Progress to {nextLevel.level}
              </span>
              <span className="text-gray-700">
                {nextLevel.required > 0
                  ? `${nextLevel.required} points needed`
                  : "Max level reached"}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${getLevelProgress()}%` }}></div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <h3 className="font-medium text-blue-800">Platform Fee</h3>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {getFeeReduction()}
              </p>
              <p className="text-sm text-gray-600">Current rate</p>
            </div>
            {!isSeller && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="h-5 w-5 text-green-600" />
                  <h3 className="font-medium text-green-800">Point Value</h3>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{((trustScore.score / 100) * 10).toFixed(2)}
                </p>
                <p className="text-sm text-gray-600">100 points = ₹10</p>
              </div>
            )}
            {isSeller && (
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="h-5 w-5 text-purple-600" />
                  <h3 className="font-medium text-purple-800">
                    Premium Status
                  </h3>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {trustScore.score >= 5000 ? "✓ Unlocked" : "Locked"}
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Free Promotions</p>
                  <p>• Priority support & badge</p>
                  {/* Live streaming benefit deferred; retain for a future release.
                  <p>• Unlimited live streaming access</p>
                  */}
                </div>
                {trustScore.score < 5000 && (
                  <p className="text-xs text-purple-600 mt-2 font-medium">
                    {5000 - trustScore.score} points to unlock
                  </p>
                )}
              </div>
            )}
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-5 w-5 text-purple-600" />
                <h3 className="font-medium text-purple-800">Next Reward</h3>
              </div>
              <p className="text-xl font-bold text-gray-900">
                {isSeller ? "Genuine Seller Badge" : "Genuine Buyer Badge"}
              </p>
              <p className="text-sm text-gray-600">
                At {nextLevel.level} level
              </p>
            </div>
          </div>

          {/* Violation Status */}
          {violations && (
            <div
              className={`mt-4 text-gray-500 p-4 rounded-lg ${
                violations.flaggedUntil
                  ? "bg-rose-200 border border-rose-500"
                  : "bg-green-50 border border-green-200"
              }`}>
              <div className="flex items-center gap-2">
                {violations.flaggedUntil ? (
                  <>
                    <AlertCircle className="h-5 w-5 text-red" />
                    <span className="font-medium text-orange-800">
                      Account Flagged ({violations.violationCount}{" "}
                      {violations.violationCount === 1
                        ? "violation"
                        : "violations"}
                      )
                    </span>
                    {violations.violationCount === 2 && (
                      <div className="text-sm text-rose-700 mt-1">
                        Final warning: Further violations may lead to a
                        permanent ban.
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <UserCheck className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">
                      Good Standing ({violations.violationCount || 0}{" "}
                      violations)
                    </span>
                  </>
                )}
              </div>
              {violations.flaggedUntil && (
                <p className="text-sm text-orange-600 mt-1">
                  Until:{" "}
                  {new Date(violations.flaggedUntil).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Referral & Buy Points Section */}
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <h3 className="font-medium text-blue-800 mb-3">
                  Earn More Points
                </h3>

                {/* Referral Program */}
                <div className="mb-4">
                  {/* Show user's code if exists */}
                  {userReferralCode && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        Your referral code:
                      </p>
                      <div className="p-3 bg-white border border-blue-300 rounded-lg flex justify-between items-center">
                        <span className="text-gray-400 font-mono">
                          {userReferralCode}
                        </span>
                        <button
                          onClick={handleCopyReferralCode}
                          className="text-blue-600 hover:text-blue-800 text-sm">
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Share this code with friends to earn 100 points when they
                        join
                      </p>
                    </div>
                  )}

                  {/* Generate referral code if user doesn't have one */}
                  {!userReferralCode && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        Refer friends and earn 100 points per join
                      </p>
                      {referralCode ? (
                        <div className="p-3 bg-white border border-blue-200 rounded-lg">
                          <p className="font-mono text-blue-600">
                            {referralCode}
                          </p>
                        </div>
                      ) : (
                        <button
                          onClick={handleGenerateReferralCode}
                          disabled={generatingCode}
                          className={`px-4 py-2 rounded-lg font-medium ${
                            generatingCode
                              ? "bg-gray-200 text-gray-500"
                              : "bg-blue-600 hover:bg-blue-700 text-white"
                          }`}>
                          {generatingCode
                            ? "Generating..."
                            : "Generate Referral Code"}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Redeem form if eligible */}
                  {!trustScore?.hasRedeemed && (
                    <>
                      {showRedeemForm ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={referralInput}
                            onChange={(e) => setReferralInput(e.target.value)}
                            placeholder="Enter friend's code"
                            className="w-full px-3 text-gray-800 py-2 border border-gray-300 rounded-md"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleRedeem}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                              Redeem 200 Points
                            </button>
                            <button
                              onClick={() => setShowRedeemForm(false)}
                              className="px-4 text-gray-700 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowRedeemForm(true)}
                          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                          Redeem Referral Code
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Buy Points Button - Better positioned */}
              {/* <div className="flex-shrink-0">
                <button
                  onClick={() => setShowBuyModal(true)}
                  disabled={isPaymentLoading}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg font-medium transition-colors w-full lg:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  <ShoppingCart className="h-5 w-5" />
                  <span>
                    {isPaymentLoading ? "Processing..." : "Buy Points"}
                  </span>
                </button>
              </div> */}

              <button
                onClick={() => setShowTipsModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium">
                <Info className="h-5 w-5" />
                <span>How to Earn Points</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Recent Activity
          </h2>
          {events.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">No activity yet</div>
              <p className="text-gray-500 text-sm">
                Your TrustScore events will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.slice(0, 10).map((event) => (
                <div
                  key={event.$id}
                  className="flex items-start border-b border-gray-100 pb-3 last:border-0">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      event.points > 0
                        ? "bg-green-100 text-green-600"
                        : "bg-rose-100 text-rose-600"
                    }`}>
                    {event.points > 0 ? "+" : ""}
                    {event.points}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-800">
                      {event.description}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(event.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Buy Points Modal - Custom implementation */}
      {showBuyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  Buy TrustPoints
                </h3>
                <button
                  onClick={() => setShowBuyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isPaymentLoading}>
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points to Buy (Minimum: 250)
                  </label>
                  <Input
                    type="number"
                    value={pointsToBuy}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setPointsToBuy(value);
                      // Clear error when user starts typing
                      if (pointsError) setPointsError("");
                    }}
                    className={`w-full text-gray-700 ${pointsError ? "border-rose-500 focus:border-rose-500" : ""}`}
                    disabled={isPaymentLoading}
                    min="250"
                    placeholder="Enter points (min 250)"
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = target.value.replace(/^0+/, "") || "0";
                    }}
                  />
                  {pointsError && (
                    <p className="text-rose-500 text-xs mt-1">{pointsError}</p>
                  )}
                </div>

                {requiresPaymentPhone(user.emailOrPhone) && (
                  <PaymentPhoneField value={paymentPhone} onChange={setPaymentPhone} />
                )}

                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="font-medium text-blue-800">Special Offer!</p>
                  <p className="text-sm text-gray-700 mt-1">
                    Get {pointsToBuy >= 250 ? pointsToBuy : 250} points (worth ₹
                    {((pointsToBuy >= 250 ? pointsToBuy : 250) / 10).toFixed(2)}
                    ) for just ₹{paymentAmount}
                  </p>
                  <p className="text-sm text-gray-700 mt-2">
                    Plus, you can qualify for a platform fee as low as <b>2%</b>{" "}
                    once you get 5000 TrustScore points.
                  </p>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <span className="font-medium text-gray-900">
                    Total: ₹{paymentAmount}
                  </span>
                  <Button
                    onClick={handleBuyPoints}
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={isPaymentLoading || pointsToBuy < 250 || !paymentContactFromIdentifier(user.emailOrPhone, paymentPhone).phone}>
                    {isPaymentLoading ? "Processing..." : "Pay Now"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showTipsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  TrustScore Point System
                </h3>
                <button
                  onClick={() => setShowTipsModal(false)}
                  className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Promotion Section */}
                <div>
                  <h4 className="text-lg font-semibold text-green-600 mb-3">
                    How to Earn Points
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Refer friends:</strong> 10 points per successful
                        referral (share your code)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Seller KYC:</strong> Complete verification for
                        50 bonus points
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Followers:</strong> 5 points per 10 followers.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Good behavior:</strong> 10 points weekly for no
                        violations
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Completed deals:</strong> 10 points per
                        successful transaction
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-green-500 mt-0.5">
                        <PlusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Positive ratings:</strong> 5 points for 4-5
                        star reviews
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Demotion Section */}
                <div>
                  <h4 className="text-lg font-semibold text-red mb-3">
                    Point Deductions
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Account flag:</strong> -100 points immediately
                        on sharing personal information.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Account ban:</strong> Reset to 50 points
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Order cancellation:</strong> -25 points (seller)
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Negative reviews:</strong> -10 points for 1-2
                        star ratings
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Lost disputes:</strong> -100 points
                      </span>
                    </li>
                    <li className="flex items-start">
                      <div className="flex-shrink-0 h-5 w-5 text-red mt-0.5">
                        <MinusCircle className="h-5 w-5" />
                      </div>
                      <span className="ml-2 text-gray-700">
                        <strong>Excessive refunds:</strong> -25 points (buyer)
                      </span>
                    </li>
                  </ul>
                </div>

                {/* TrustScore Benefits */}
                <div>
                  <h4 className="text-lg font-semibold text-blue-600 mb-3">
                    TrustScore Benefits
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-medium text-blue-800">For Buyers</h5>
                      <ul className="mt-2 space-y-2 text-sm text-gray-700">
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Lower platform fees (down to 2%)
                        </li>
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Priority customer support
                        </li>
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Early access to new features
                        </li>
                      </ul>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-medium text-purple-800">
                        For Sellers
                      </h5>
                      <ul className="mt-2 space-y-2 text-sm text-gray-700">
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Reduced platform fees (down to 2%)
                        </li>
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Featured in search results
                        </li>
                        <li className="flex items-start">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 mr-1.5" />
                          Higher transaction limits
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowTipsModal(false)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  Got It!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrustScoreDashboard;
